import { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  createCapitalCounselServices,
  createPurchaseAccompanyingServices,
  createTransferAgencyServices,
} from '@/api/AdditionalServices/createAdditionalServices';
import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import purchaseAccompanyingTruckIcon from '@/assets/images/purchase-accompanying-truck.png';
import BasicButton from '@/components/Common/Button/BasicButton';
import IconInfoButton from '@/components/Common/Button/IconInfoButton';
import ImgIconInfoButton from '@/components/Common/Button/ImgIconInfoButton';
import PhoneCallFloationgButton from '@/components/Common/Button/PhoneCallFloationgButton';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import CarError from '@/components/Error/CarError';
import Error from '@/components/Error/Error';
import MenuHeader, { MenuHeaderIcon } from '@/components/Header/MenuHeader';
import {
  DocumentTextSolidIcon,
  EnlargementOutlineIcon,
  ErrorCircleIcon,
  HomeOutlineIcon,
  InformationCircleIcon,
  LoanConsulationSolidIcon,
  PencilIcon,
  ShareOutlineIcon,
} from '@/components/Icon';
import { MoreMenuData } from '@/components/Menu/MoreMenu';
import CapitalGuideView from '@/components/More/CaptialCounselService/CapitalGuideView';
import ProductStatus from '@/components/More/ProductStatus';
import AccompanyingGuidePopup from '@/components/More/PurchaseAccompanyingService/AccompanyingGuidePopup';
import TransferGuidePopup from '@/components/More/TransferAgencyService/TransferGuidePopup';
import MyProductPrice from '@/components/Products/MyProductPrice';
import ProductDetailFooter from '@/components/Products/Purchase/ProductDetailFooter';
import ProductShareBottomSheet from '@/components/Products/Purchase/ProductShareBottomSheet';
import ProductDetailTab from '@/components/Products/Purchase/ProductTab';
import HistoryCountView from '@/components/Products/Sale/HistoryCountView';
import SaleTipBox from '@/components/Products/Sale/SaleTipBox';
import {
  IS_ALREADY_APPLY_CAPITAL_COUNSEL_SERVICE,
  IS_ALREADY_APPLY_PURCHASE_ACCOMPANYING_SERVICE,
  IS_ALREADY_APPLY_TRANSFER_AGENCY_SERVICE,
} from '@/const/additionalService';
import { colors } from '@/const/colors';
import { NAVER_WCS_ACCOUNT_ID, PRODUCT_DETAIL } from '@/const/common';
import { ALREADY_SALE_PRODUCTS, DELETE_PRODUCTS } from '@/const/errorCode';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { INDIVIDUAL, INDIVIDUAL_DELIVERY, INDIVIDUAL_HIGH, INDIVIDUAL_MIDDLE, INDIVIDUAL_SMALL } from '@/const/license';
import { SALE, WAITING } from '@/const/productStatus';
import {
  SALES_TYPE_ASSURANCE,
  SALES_TYPE_CONSIGNMENT,
  SALES_TYPE_NORMAL,
  SALES_TYPE_THIRD_PARTY_DEALER,
} from '@/const/products';
import { THEUNBAN } from '@/const/referralCode';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import { useLoanCalculator } from '@/hooks/useLoanCalculator';
import usePartnerSession from '@/hooks/usePartnerSession';
import ownerVerificationMedal from '@/assets/images/owner-verification-medal.png';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import NotFoundPage from '@/pages/Error/NotFoundPage';
import { isMemberLoadedAtom, memberAtom, updateAdditionalServiceAtom } from '@/store/member';
import { canModifyProduct } from '@/utils/Products/productUtils';
import { formatNumber, formatPrice } from '@/utils/common';
import Flicking, { ChangedEvent } from '@egjs/react-flicking';

type LiceseProps = {
  truckNumber: string | undefined;
};

const LicensePlate = ({ truckNumber }: LiceseProps) => {
  return (
    <div className="border-2 rounded-[4px] border-gray-8 bg-yellow w-fit">
      <div className="m-[2px] border rounded-[4px]  border-gray-8 text-[10px] font-bold text-gray-8 px-[7px] py-1">
        {truckNumber}
      </div>
    </div>
  );
};

const ProductDetail = () => {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isNearBottom, setIsNearBottom] = useState(false);
  const threshold = 20;

  const [isAlreadyApplyCapitalService, setIsAlreadyApplyCaptialService] = useState(false);
  const [isAlreadyApplyTransferService, setIsAlreadyApplyTransferService] = useState(false);
  const [isAlreadyApplyAccompanyingService, setIsAlreadyApplyAccompanyingService] = useState(false);

  const [isMaintenanceSale, setIsMaintenanceSale] = useState(false);

  const isTheunban = usePartnerSession() === THEUNBAN;
  const authRedirect = useAuthRedirect();
  const isMemberLoaded = useAtomValue(isMemberLoadedAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const flickingRef = useRef<Flicking>(null);
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [memberAtomData] = useAtom(memberAtom);
  const [, updateAdditionalService] = useAtom(updateAdditionalServiceAtom);

  const isRegister = location.state?.isRegisterBtn;
  const isPublicDetail = location.state?.isPublicDetail == null ? true : location.state?.isPublicDetail;
  const [isSeller, setIsSeller] = useState<boolean>();
  const [isShowBasicPopup, setIsShowBasicPopup] = useState<boolean>(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [tabNum, setTabNum] = useState(0);

  const [moreMenuData, setMoreMenuData] = useState<MoreMenuData>();
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const [isShowErrorPageForDeleteOrCompletedProduct, setIsShowErrorPageForDeleteOrCompletedProduct] = useState(false);
  const { showToast } = useToastContext();

  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editingPriceValue, setEditingPriceValue] = useState<string>('');
  const [isSavingPrice, setIsSavingPrice] = useState(false);

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: '',
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [isConfirmModalConfig, setIsConfirmModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const [isOpenCapitalModal, setIsOpenCapitalModal] = useState(false);
  const [isOpenTransferModal, setIsOpenTransferModal] = useState(false);
  const [isOpenAccompanyingModal, setIsOpenAccompanyingModal] = useState(false);
  const [isOpenShareBottomSheet, setIsOpenShareBottomSheet] = useState(false);

  const productDetailPath = isPublicDetail ? '/api/v1/public/products/' : '/api/v1/products/';

  const fetchProductDetail = async (id?: string, memberId?: number) => {
    const queryParams = new URLSearchParams();
    if (memberId) {
      queryParams.append('memberId', String(memberId));
    }

    // if (isPublicDetail) {
    //   const response = await newApiManager.get(`${productDetailPath}${id}?` + queryParams.toString());
    //   return response.data;
    // } else {
    //   const response = await apiManager.get(`${productDetailPath}${id}?` + queryParams.toString());
    //   return response.data;
    // }

    const response = await newApiManager.get(`${productDetailPath}${id}?` + queryParams.toString());
    return response.data;
  };

  const { data, error, isLoading, refetch } = useQuery(
    ['productDetail', id, memberAtomData?.id],
    () => fetchProductDetail(id, memberAtomData?.id),
    {
      onError: (error: AxiosError) => {
        if (error?.code === ALREADY_SALE_PRODUCTS || error?.code === DELETE_PRODUCTS) {
          setIsShowErrorPage(false);
          setIsShowErrorPageForDeleteOrCompletedProduct(true);
        } else {
          setIsShowErrorPage(true);
          setIsShowErrorPageForDeleteOrCompletedProduct(false);
        }
      },
      enabled: isMemberLoaded,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      placeholderData: (prevData: any) => prevData,
      retry: 0,
    },
  );

  useEffect(() => {
    if (data) {
      setMoreMenuData({
        id: data.id,
        status: data.status.code,
        productsType: data.type?.code,
      });

      if (data?.salesType?.code === 'ASSURANCE' && data?.status?.code === 'MAINTENANCE_SALE') {
        setIsMaintenanceSale(true);
      }

      setIsSeller(data?.sellerId === memberAtomData?.id);
    }

    if (error) {
      const errorResponse = error as ErrorResponse;
      if (errorResponse.code === ALREADY_SALE_PRODUCTS || errorResponse.code === DELETE_PRODUCTS) {
        setIsShowErrorPage(false);
        setIsShowErrorPageForDeleteOrCompletedProduct(true);
      } else {
        setIsShowErrorPage(true);
        setIsShowErrorPageForDeleteOrCompletedProduct(false);
      }
    }
  }, [data, error]);

  const { loanCalculatorState, handleInputChange, handleInputBlur, setLoanCalculatorState } = useLoanCalculator(
    data?.price,
  );

  useEffect(() => {
    if (!data?.price) return;
    setLoanCalculatorState((prev) => ({
      ...prev,
      principal: String(data.price * 10000),
    }));
  }, [data?.price, setLoanCalculatorState]);

  // 네이버 웹로그 분석 스크립트 로드 및 실행
  useEffect(() => {
    if (!data) return;

    // wcslog.js 스크립트 로드
    const loadWcsScript = () => {
      return new Promise<void>((resolve, reject) => {
        // 이미 로드되어 있는지 확인
        const wcs = (window as any).wcs;
        if (wcs) {
          resolve();
          return;
        }

        // 스크립트가 이미 추가되어 있는지 확인
        const existingScript = document.querySelector('script[src*="wcslog.js"]');
        if (existingScript) {
          // 스크립트가 있지만 window.wcs가 아직 없는 경우, 로드 완료를 기다림
          const checkInterval = setInterval(() => {
            if ((window as any).wcs) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if ((window as any).wcs) {
              resolve();
            } else {
              const error: Error = { name: 'WCSLoadError', message: 'WCS script load timeout' };
              reject(error);
            }
          }, 5000);
          return;
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = '//wcs.naver.net/wcslog.js';
        script.async = true;
        script.onload = () => {
          // 스크립트 로드 후 window.wcs가 준비될 때까지 대기
          const checkInterval = setInterval(() => {
            if ((window as any).wcs) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if ((window as any).wcs) {
              resolve();
            } else {
              const error: Error = { name: 'WCSNotFoundError', message: 'WCS object not found' };
              reject(error);
            }
          }, 5000);
        };
        script.onerror = () => {
          const error: Error = { name: 'WCSLoadError', message: 'Failed to load WCS script' };
          reject(error);
        };
        document.head.appendChild(script);
      });
    };

    // 네이버 웹로그 트래킹 실행
    const executeNaverTracking = () => {
      const wcs = (window as any).wcs;
      if (!wcs) return;

      if (!(window as any).wcs_add) {
        (window as any).wcs_add = {};
      }

      (window as any).wcs_add['wa'] = NAVER_WCS_ACCOUNT_ID;

      const category = data?.manufacturerCategories?.name || data?.model?.name || '';
      const option = data?.carOption
        ? [
            ...(data.carOption.normalOption?.option?.map((opt: any) => opt.desc) || []),
            ...(data.carOption.additionalOption?.option?.map((opt: any) => opt.desc) || []),
            ...(data.carOption.breakOption?.option?.map((opt: any) => opt.desc) || []),
          ].join(', ')
        : '';

      const _conv: any = {
        type: 'view_product',
        items: [
          {
            id: String(data.id),
            name: data.truckName || '',
            quantity: '1',
          },
        ],
      };

      wcs.trans(_conv);
    };

    loadWcsScript()
      .then(() => {
        executeNaverTracking();
      })
      .catch((error) => {
        console.error('네이버 웹로그 스크립트 로드 실패:', error);
      });
  }, [data]);

  useEffect(() => {
    if (data) {
      if (isRegister) {
        showToast('차량이 등록되었어요.', 'success', 'bottom');
        queryClient.invalidateQueries({ queryKey: ['product-search'] });
      } else if (isRegister === false) {
        showToast('차량이 수정되었어요.', 'success', 'bottom');
        queryClient.invalidateQueries({ queryKey: ['product-search'] });
      }
    }
  }, [data]);

  const productImages = useMemo(() => {
    const list: string[] = [];
    if (!data?.productsImage) return list;

    const images = data.productsImage;
    const imageFields = [
      'frontSideImageUrl',
      'backSideImageUrl',
      'frontImageUrl',
      'backImageUrl',
      'tireImageUrl',
      'engineImageUrl',
      'insideImageUrl',
      'dashboardImageUrl',
      'sheetImageUrl',
    ];

    imageFields.forEach((field) => {
      const url = (images as any)[field];
      if (url && url !== '') list.push(url);
    });

    if (images.optionImageUrl) {
      images.optionImageUrl.forEach((item: any) => {
        if (item !== '') list.push(item);
      });
    }

    return list;
  }, [data?.productsImage]);

  const isConsignmentSale = data?.salesType?.code === SALES_TYPE_CONSIGNMENT;
  const isNotDirectProduct = data?.salesType?.code === SALES_TYPE_ASSURANCE || isConsignmentSale;

  const onClickImage = () => {
    navigate(`/products/${id}/image`, { state: { title: data?.truckNumber, imageList: productImages } });
  };

  const menuHeaderIcons: MenuHeaderIcon[] = [
    { icon: <HomeOutlineIcon color={colors.gray[8]}></HomeOutlineIcon>, onClickIcon: () => navigate('/') },
    {
      icon: <ShareOutlineIcon color={colors.gray[8]}></ShareOutlineIcon>,
      onClickIcon: () => setIsOpenShareBottomSheet(true),
    },
  ];

  const getSalesTypeMessage = (type: string) => {
    switch (type) {
      case SALES_TYPE_NORMAL:
        return (
          <div className="text-[14px]">
            개인 차주가 판매 중인 <br className="hidden sm280:block" />
            <span className="font-bold text-primary-10 break-keep">직거래 차량</span>입니다.
          </div>
        );
      case SALES_TYPE_CONSIGNMENT:
        return (
          <div className="text-[14px]">
            판매자 요청에 의해 직트럭이 {' '}
            <span className="font-bold break-keep bg-blueGradient-blue-to-blue bg-clip-text text-transparent inline-block">
              알선판매중인 차량
            </span>
            입니다.
          </div>
        );
      case SALES_TYPE_ASSURANCE:
        return isMaintenanceSale ? (
          <>
            <div className="text-[14px]">
              입고되어 점검 및 정비 진행 중인
              <span className="font-bold text-green-0 break-keep ml-1">직트럭 상품용 차량 </span>
              입니다.
            </div>
          </>
        ) : (
          <>
            <img
              src={`https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/InspectionCompleted.png`}
              alt="inspection-completed"
              width={32}
              height={32}
            />
            <div className="text-[14px]">
              점검 및 수리 후 상품화가 완료된 <br className="hidden sm360:block" />
              <span className="font-bold text-green-0 break-keep">직트럭 상품용 차량</span>
              입니다.
            </div>
          </>
        );
      case SALES_TYPE_THIRD_PARTY_DEALER:
        return (
          <div className="flex flex-col gap-1">
            <div className="text-[14px]">
              타 매매상사의 딜러가 판매 중인 <span className="font-bold text-gray-8 break-keep">타사딜러 차량</span>
              입니다.
            </div>
            <p className="text-[14px] text-gray-7">
              ※ 직트럭은 등록 시스템만을 제공하며, 판매자가 직접 등록한 차량의 모든 책임은 판매자에게 있습니다.
            </p>
          </div>
        );
      default:
        return '';
    }
  };

  const handleFlickingChanged = (e: ChangedEvent<Flicking>) => {
    const { index } = e.currentTarget;

    setCurrentImageIndex(index);
  };

  const handleCloseModal = () => {
    setIsOpenCapitalModal(false);
    setIsOpenTransferModal(false);
    setIsOpenAccompanyingModal(false);
    setIsNearBottom(false);
  };

  const handleClickApply = () => {
    if (!authRedirect(PRODUCT_DETAIL(data.id), { feature: '서비스 신청' })) {
      return;
    }

    setIsShowBasicPopup(true);

    setModalConfig({
      title: `${data.truckName}`,
      content: (
        <>
          해당 차량으로{' '}
          {isOpenCapitalModal
            ? '화물차 대출 상담'
            : isOpenTransferModal
            ? '서류 이전 대행'
            : isOpenAccompanyingModal
            ? '차량 구매 동행'
            : ''}
          <br />
          서비스를 신청할까요?
        </>
      ),
      leftButtonLabel: '취소',
      rightButtonLabel: '신청하기',
      onLeftButtonClick: () => setIsShowBasicPopup(false),
      onRightButtonClick: handleClickApplyOk,
    });
  };

  const handleClickApplyOk = () => {
    if (isOpenCapitalModal) {
      const requestData = {
        name: memberAtomData?.name,
        phoneNumber: memberAtomData?.phoneNumber,
        productId: data.id,
      };

      postCapitalCounselServices.mutate(requestData);
    } else if (isOpenTransferModal) {
      const requestData = {
        name: memberAtomData?.name,
        phoneNumber: memberAtomData?.phoneNumber,
      };

      postTransferAgencyServices.mutate(requestData);
    } else if (isOpenAccompanyingModal) {
      const requestData = {
        name: memberAtomData?.name,
        phoneNumber: memberAtomData?.phoneNumber,
        productId: data.id,
      };

      postPurchaseAccompanyingServices.mutate(requestData);
    }

    setIsShowBasicPopup(false);
  };

  const postCapitalCounselServices = useMutation(
    (request: AdditionalServicesApplyRequest) => createCapitalCounselServices(request),
    {
      onSuccess: () => {
        showToast('화물차 대출 상담 서비스를 신청했어요.', 'success', 'bottom');
        setIsAlreadyApplyCaptialService(true);
        updateAdditionalService(IS_ALREADY_APPLY_CAPITAL_COUNSEL_SERVICE);
      },
      onError: (error: AxiosError) => {
        if (error.code && error.message) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
      onSettled: () => {
        setIsOpenCapitalModal(false);
      },
    },
  );

  const postTransferAgencyServices = useMutation(
    (request: AdditionalServicesApplyRequest) => createTransferAgencyServices(request),
    {
      onSuccess: () => {
        showToast('서류 이전 대행 서비스를 신청했어요.', 'success', 'bottom');
        setIsAlreadyApplyTransferService(true);
        updateAdditionalService(IS_ALREADY_APPLY_TRANSFER_AGENCY_SERVICE);
      },
      onError: (error: AxiosError) => {
        if (error.code && error.message) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
      onSettled: () => {
        setIsOpenTransferModal(false);
      },
    },
  );

  const postPurchaseAccompanyingServices = useMutation(
    (request: AdditionalServicesApplyRequest) => createPurchaseAccompanyingServices(request),
    {
      onSuccess: () => {
        showToast('차량 구매 동행 서비스를 신청했어요.', 'success', 'bottom');
        setIsAlreadyApplyAccompanyingService(true);
        updateAdditionalService(IS_ALREADY_APPLY_PURCHASE_ACCOMPANYING_SERVICE);
      },
      onError: (error: AxiosError) => {
        if (error.code && error.message) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
      onSettled: () => {
        setIsOpenAccompanyingModal(false);
      },
    },
  );

  const handleScrollBottom = () => {
    const popupElement = popupRef.current;
    const contentElement = contentRef.current;

    if (!popupElement) return;
    if (!contentElement) return;

    const scrollTop = popupElement.scrollTop;
    const viewportHeight = popupElement.clientHeight;
    const scrollHeight = contentElement.scrollHeight;

    if (scrollTop + viewportHeight < scrollHeight) {
      popupElement.scrollBy({
        top: viewportHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    const popupElement = popupRef.current;
    const contentElement = contentRef.current;

    if (!popupElement) return;
    if (!contentElement) return;

    const scrollTop = popupElement.scrollTop;
    const viewportHeight = popupElement.clientHeight;
    const scrollHeight = contentElement.scrollHeight;

    const nextIsNearBottom = scrollTop + viewportHeight >= scrollHeight - threshold;
    setIsNearBottom((prev) => (prev === nextIsNearBottom ? prev : nextIsNearBottom));
  };

  useEffect(() => {
    setTimeout(() => {
      handleScroll();
    }, 0);
  }, [isOpenTransferModal, isOpenCapitalModal, isOpenAccompanyingModal]);

  useEffect(() => {
    if (memberAtomData) {
      setIsAlreadyApplyTransferService(memberAtomData.isAlreadyApplyTransferAgencyService);
      setIsAlreadyApplyCaptialService(memberAtomData.isAlreadyApplyCapitalCounselService);
      setIsAlreadyApplyAccompanyingService(memberAtomData.isAlreadyApplyPurchaseAccompanyingService);
    }
  }, [memberAtomData]);

  const isDealerType = memberAtomData?.type ? memberAtomData.type?.code !== 'NORMAL' : false;
  const canEditPrice = isSeller && !isDealerType && canModifyProduct(data?.type?.code);
  const isShowMoreMenuIcon = isSeller && !isTheunban && data?.salesType?.code !== SALES_TYPE_CONSIGNMENT;
  const priceDownAmount = data?.oldPrice && data?.price ? data.oldPrice - data.price : 0;
  const isPriceDown = data?.status?.code === SALE && priceDownAmount > 0;

  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/[^\d]/g, '');
    const limited = onlyDigits.slice(0, 5);
    setEditingPriceValue(limited);
  };

  const cancelPriceEditor = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditingPrice(false);
    setEditingPriceValue('');
    setIsSavingPrice(false);
  };

  const performSavePrice = async () => {
    if (!data || isSavingPrice) return;

    const cleaned = (editingPriceValue || '').replace(/[^\d]/g, '');
    const nextPrice = Number(cleaned);

    if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
      showToast('판매 가격을 올바르게 입력해 주세요.', 'error', 'bottom');
      return;
    }

    const approvedPrice = data.price ?? 0;
    if (data.approvalStatusList?.at(-1)?.status?.code === 'APPROVAL' && nextPrice > approvedPrice) {
      showToast('가격은 그 이상으로 수정이 불가능합니다.', 'error', 'bottom');
      return;
    }

    try {
      setIsSavingPrice(true);
      await newApiManager.patch(`/api/v1/products/${data.id}`, { price: nextPrice });
      await refetch();
      if (nextPrice <= approvedPrice - 50) {
        setIsConfirmModal(true);
        setIsConfirmModalConfig({
          title: '차량 판매 금액 수정 완료',
          content: (
            <>
              <div className="mb-4">
                50만원 이상 수정되어 <br />
                해당 차량이 상단에 노출돼요.
              </div>
            </>
          ),
          leftButtonLabel: '',
          rightButtonLabel: '확인',
          onLeftButtonClick: () => {},
          onRightButtonClick: () => {
            setIsConfirmModal(false);
          },
        });
      } else {
        showToast('판매 가격이 변경되었어요.', 'success', 'bottom');
      }
      cancelPriceEditor();
    } catch (error) {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      setIsSavingPrice(false);
    }
  };

  const openPriceEditor = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!data || data.status.code !== SALE || !canEditPrice) return;
    setIsEditingPrice(true);
    setEditingPriceValue(data.price ? String(data.price) : '');
  };

  const savePrice = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await performSavePrice();
  };

  const onPriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void performSavePrice();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelPriceEditor();
    }
  };

  return (
    <>
      <DefaultHelmet page="purchase" />
      {data && !isShowErrorPage && !isShowErrorPageForDeleteOrCompletedProduct && (
        <>
          <MenuHeader
            title={data.truckNumber || ''}
            icons={isTheunban ? [] : menuHeaderIcons}
            moreMenuIcon={isShowMoreMenuIcon}
            moreMenuData={moreMenuData}
            isMain={isTheunban ? true : false}
            price={data.price}
          />
          <section className="flex flex-col justify-between w-full pb-28 pt-[60px]">
            <div className="relative">
              <Flicking
                ref={flickingRef}
                align="center"
                circular={true}
                renderOnlyVisible={true}
                className="z-0 w-full"
                moveType="strict"
                circularFallback="bound"
                onChanged={(e) => handleFlickingChanged(e)}
              >
                {productImages.map((imageUrl, index) => (
                  <div
                    className="relative flex w-full h-[16.875rem]"
                    key={index}
                    onClick={() => {
                      onClickImage();
                    }}
                  >
                    <img src={imageUrl as string} className="object-cover w-full h-full" alt="product_img"></img>
                  </div>
                ))}
              </Flicking>
              {isConsignmentSale && (
                <div className="absolute bottom-2 left-3 z-20 flex items-center gap-1.5 pointer-events-none bg-gray-9 opacity-70 rounded-full px-3 py-1.5">
                  <img src={ownerVerificationMedal} className="w-6 h-6" alt="소유주 인증" />
                  <span className="text-white text-[14px] leading-[16px] font-medium">소유주 인증</span>
                </div>
              )}
              <div
                className="absolute bottom-0 left-0 w-full h-[50px] p-3 bg-gradient-to-t from-[rgba(0,0,0,0.4)] to-[rgba(0,0,0,0)]"
                onClick={() => {
                  onClickImage();
                }}
              >
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10 bg-gray-9 opacity-70 rounded-full text-white min-w-[46px] h-auto text-center text-[14px] leading-[16px] font-medium p-2">
                  {currentImageIndex + 1}/{productImages?.length}
                </div>
                <div className="absolute bottom-0 right-0 z-10 p-3">
                  <EnlargementOutlineIcon color={colors.gray[0]} />
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-2">
                <ProductStatus
                  approvalStatus={data.approvalStatusList?.at(-1)?.status}
                  status={data.status}
                  id={data.id}
                  detailStatusStyle={true}
                  path="products"
                  price={data.price}
                  refetch={refetch}
                  selectBoxBoolean={!!isSeller && canModifyProduct(data?.type?.code)}
                />
              </div>
              <div className="relative flex items-start justify-between">
                <span className="text-xl font-semibold align-middle">{data.truckName}</span>
              </div>
              <span className="pt-2 text-gray-6">매물번호 {data.productsNumber}</span>
              <div className="mb-2">
                {isPriceDown && !isEditingPrice && (
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-[16px] text-[#9CA3AF] line-through">
                      {formatPrice(String(data.oldPrice))}
                    </span>
                    <span className="bg-[#EF4444] text-gray-0 rounded-md px-2 py-[3px] text-[14px] font-bold">
                      -{formatPrice(String(priceDownAmount))} 인하
                    </span>
                  </div>
                )}
                {canEditPrice && data.status.code === SALE ? (
                  isEditingPrice ? (
                    <div
                      className="mt-1 flex flex-col gap-2 sm450:flex-row sm450:items-center sm450:justify-start"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative w-full sm450:w-[170px]">
                        <input
                          className="w-full px-2 py-1 text-xl font-semibold text-gray-9 border border-gray-3 rounded-md"
                          value={editingPriceValue ? formatNumber(editingPriceValue, true) : ''}
                          onChange={handlePriceInputChange}
                          onKeyDown={onPriceKeyDown}
                          inputMode="numeric"
                          placeholder="판매 가격"
                          autoFocus
                        />
                        <span className="absolute top-0 right-2 flex justify-center items-center h-full">만원</span>
                      </div>
                      <div className="flex smax450:w-full gap-2 sm450:max-w-[200px] sm450:flex-1 break-keep">
                        <button
                          className="w-full h-[36px] smax450:w-full smax450:h-[32px] smax450:mb-1 px-2 py-1 text-sm font-semibold rounded-md bg-primary-10 text-white"
                          onClick={savePrice}
                          disabled={isSavingPrice}
                        >
                          저장
                        </button>
                        <button
                          className="w-full h-[36px] smax450:w-full smax450:h-[32px] px-2 py-1 text-sm font-semibold rounded-md bg-gray-3 text-gray-8"
                          onClick={cancelPriceEditor}
                          disabled={isSavingPrice}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-semibold">
                        <MyProductPrice product={data} />
                      </span>
                      {data.status.code === SALE && data.approvalStatusList?.at(-1)?.status?.code !== WAITING && (
                        <button
                          onClick={openPriceEditor}
                          aria-label="판매 가격 수정"
                          className="text-primary-10 text-sm font-semibold bg-secondaryBlue-0 px-2 py-1 rounded-md border border-primary-10"
                        >
                          {/* <PencilIcon color={colors.gray[8]} width={16} height={16} /> */}
                          가격 수정
                        </button>
                      )}
                    </div>
                  )
                ) : (
                  <div className="flex items-end">
                    <p className="text-xl font-semibold">
                      <MyProductPrice product={data} />
                    </p>
                  </div>
                )}

                {isSeller && (
                  <div className="mt-4">
                    <SaleTipBox />
                  </div>
                )}

                {data?.salesType && (
                  <>
                    <div className="border border-gray-3 rounded-[12px] px-4 py-[14px] my-4">
                      {isMaintenanceSale ? (
                        getSalesTypeMessage(data?.salesType?.code)
                      ) : (
                        <div className="flex items-center gap-2">{getSalesTypeMessage(data?.salesType?.code)}</div>
                      )}
                    </div>

                    {isMaintenanceSale && data?.productsSalesNotice && (
                      <div className="border border-grey-3 bg-grey-2 rounded-[12px] px-4 py-[14px] my-4 text-[14px]">
                        {data?.productsSalesNotice}
                      </div>
                    )}
                  </>
                )}
              </div>
              {(data?.license?.licenseType?.code === INDIVIDUAL_SMALL ||
                data?.license?.licenseType?.code === INDIVIDUAL_MIDDLE ||
                data?.license?.licenseType?.code === INDIVIDUAL_HIGH ||
                data?.license?.licenseType?.code === INDIVIDUAL_DELIVERY) && (
                <div className="bg-gray-1 rounded-[12px] p-4 mb-6">
                  <div className="sm680:flex sm680:gap-2">
                    <LicensePlate truckNumber={data.truckNumber} />
                    <div className="text-gray-11 font-medium text-[15px] mt-[6px]">
                      {data?.license?.licenseType?.desc} 번호판 별도 판매중
                    </div>
                  </div>
                  <div className="w-full h-[1px] bg-gray-4 my-3"></div>
                  <div className="flex">
                    <InformationCircleIcon color={colors.gray[6]} width={16} height={16} />
                    <div className="text-gray-7 leading-[16px] text-[13px] flex-1 ml-1">
                      판매자가 {data?.license?.licenseType?.desc} 번호판까지 함께 판매하고 있어, 번호판이 판매되지
                      않으면 거래 일정이 지연될 수 있습니다.
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-7">조회수 {data.hit}</p>
            </div>

            {data?.youtubeUrl && (
              <>
                <div className="w-full h-2 bg-gray-1"></div>
                <div className="p-4">
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    {(() => {
                      const youtubeUrl = data.youtubeUrl;
                      const isMp4 =
                        typeof youtubeUrl === 'string' && (youtubeUrl.endsWith('.mp4') || youtubeUrl.includes('.mp4?'));

                      const src = isMp4 ? youtubeUrl : `https://www.youtube.com/embed/${youtubeUrl}`;

                      return (
                        <iframe
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          src={src}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            <div className="py-6 border-t-8 border-gray-1">
              <p className="text-gray-8 font-semibold text-[18px] px-4 mb-3">이런 서비스는 어떠세요?</p>
              <div className="px-4 flex flex-col sm600:flex-row gap-[12px]">
                <ImgIconInfoButton
                  onClick={() => setIsOpenCapitalModal(true)}
                  img="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/coin.png"
                  subtitle={`이 차량을 ${loanCalculatorState.loanTerm}개월 최저금리 ${loanCalculatorState.interestRate}% 적용시`}
                  mainTitle={`월 납부금 ${formatNumber(loanCalculatorState.monthlyPayment)}원`}
                  description="1분 안에 신청하기!"
                />
                <ImgIconInfoButton
                  onClick={() => setIsOpenTransferModal(true)}
                  img="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/documents.png"
                  subtitle="귀찮은 서류 업무 한번에!"
                  mainTitle="서류 이전 대행 서비스"
                />
                {!isSeller && !isNotDirectProduct && (
                  <ImgIconInfoButton
                    onClick={() => setIsOpenAccompanyingModal(true)}
                    img={purchaseAccompanyingTruckIcon}
                    subtitle="차량을 안전하게 구매하고 싶으시다면?"
                    mainTitle="구매동행 서비스"
                  />
                )}
              </div>
            </div>

            <HistoryCountView
              productData={data}
              onChange={setTabNum}
              tabMapping={{ SEIZURE: 1, MORTGAGE: 1, TRADING: 1, TUNING: 1 }}
            />
            <div className="w-full h-2 bg-gray-1"></div>

            <ProductDetailTab product={data} value={tabNum} onChange={setTabNum} />
          </section>
          {!isSeller && (
            <div className="flex items-center justify-center fixed w-full mx-auto my-0 bottom-0 left-0 right-0 h-16 max-w-[720px] min-w-[280px] z-40">
              <ProductDetailFooter product={data} />
            </div>
          )}

          <ProductShareBottomSheet
            isOpen={isOpenShareBottomSheet}
            onClose={() => setIsOpenShareBottomSheet(false)}
            shareUrl={`${window.location.origin}${PRODUCT_DETAIL(data.id)}`}
            truckNumber={data.truckNumber}
            truckName={data.truckName}
            price={data.price}
            imageUrl={productImages[0]}
          />
        </>
      )}
      {isShowErrorPage && <NotFoundPage />}
      {isShowErrorPageForDeleteOrCompletedProduct && (
        <div className="flex flex-col items-center justify-center w-full h-screen">
          <CarError
            img="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/car-error.png"
            title="찾을 수 없는 차량입니다"
            subTitle="현재 판매중이지 않는 차량입니다."
            description="하단 버튼을 클릭하면 </br>많은 차량을 보실 수 있어요."
          />
        </div>
      )}

      <ConfirmModal
        isOpen={isShowBasicPopup}
        title={modalConfig.title}
        content={modalConfig.content}
        leftButtonLabel={modalConfig.leftButtonLabel}
        rightButtonLabel={modalConfig.rightButtonLabel}
        onLeftButtonClick={modalConfig.onLeftButtonClick}
        onRightButtonClick={modalConfig.onRightButtonClick}
      />

      <ConfirmModal
        isOpen={isConfirmModal}
        title={isConfirmModalConfig.title}
        content={isConfirmModalConfig.content}
        leftButtonLabel={isConfirmModalConfig.leftButtonLabel}
        rightButtonLabel={isConfirmModalConfig.rightButtonLabel}
        onLeftButtonClick={isConfirmModalConfig.onLeftButtonClick}
        onRightButtonClick={isConfirmModalConfig.onRightButtonClick}
      />

      <BottomSheetPopup
        ref={popupRef}
        isOpen={isOpenCapitalModal || isOpenTransferModal || isOpenAccompanyingModal}
        onClose={handleCloseModal}
        title={
          isOpenCapitalModal
            ? '화물차 대출 상담 서비스'
            : isOpenTransferModal
            ? '서류 이전 대행 서비스'
            : isOpenAccompanyingModal
            ? '구매 동행 서비스'
            : ''
        }
        onScroll={handleScroll}
      >
        <div className={isOpenAccompanyingModal ? '' : 'pt-4 pb-2'}>
          {isOpenCapitalModal ? (
            <>
              {/* <CapitalGuidePopup ref={contentRef} price={data.price} /> */}

              <div className="pb-[56px]">
                <CapitalGuideView ref={contentRef} price={data?.price || 0} isPopup />
              </div>

              <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
                <div className="absolute right-[16px] top-[-72px]">
                  <PhoneCallFloationgButton />
                </div>

                <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
                <BasicButton
                  name={
                    isAlreadyApplyCapitalService
                      ? '서비스 신청완료'
                      : isNearBottom
                      ? '한도 조회 신청하기'
                      : '아래로 내리기'
                  }
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  isDisabled={isAlreadyApplyCapitalService}
                  onClick={isNearBottom ? handleClickApply : handleScrollBottom}
                />
              </div>
            </>
          ) : isOpenTransferModal ? (
            <>
              <TransferGuidePopup ref={contentRef} />
              <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
                <div className="absolute right-[16px] top-[-72px]">
                  <PhoneCallFloationgButton />
                </div>

                <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
                <BasicButton
                  name={
                    isAlreadyApplyTransferService
                      ? '서비스 신청완료'
                      : isNearBottom
                      ? '서비스 신청하기'
                      : '아래로 내리기'
                  }
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  isDisabled={isAlreadyApplyTransferService}
                  onClick={isNearBottom ? handleClickApply : handleScrollBottom}
                />
              </div>
            </>
          ) : isOpenAccompanyingModal ? (
            <>
              <AccompanyingGuidePopup ref={contentRef} />
              <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-grey-2">
                <div className="absolute right-[16px] top-[-72px]">
                  <PhoneCallFloationgButton />
                </div>

                <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent bg-grey-2"></div>
                <BasicButton
                  name={
                    isAlreadyApplyAccompanyingService
                      ? '서비스 신청완료'
                      : isNearBottom
                      ? '서비스 신청하기'
                      : '아래로 내리기'
                  }
                  bgColor={colors.primary[10]}
                  borderColor={colors.primary[10]}
                  textColor={colors.gray[0]}
                  fontSize={16}
                  height={48}
                  fontWeight="bold"
                  isDisabled={isAlreadyApplyAccompanyingService}
                  onClick={isNearBottom ? handleClickApply : handleScrollBottom}
                />
              </div>
            </>
          ) : null}
        </div>
      </BottomSheetPopup>
    </>
  );
};

export default ProductDetail;
